import { useCallback, useEffect, useRef, useState } from "react";
import {
  RoutingJobCreate,
  RoutingJobStatus,
  RoutingVehicleCreate,
  useCreateRoutingJobMutation,
  useRoutingJobByIdQuery,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useCourieStore } from "@src/common/lib/store";

const POLL_INTERVAL_MS = 2500;
const PENDING_TIMEOUT_MS = 60_000;
const PENDING_MESSAGE =
  "Transfer in progress. This may take a few minutes—check back soon.";

interface PendingJob {
  id: string;
  stopDates: string[];
}

interface UseTransferRoutingJobParams {
  // The job has been applied for these dates.
  onApplied: (stopDates: string[]) => void;
  // The job is still queued after the wait limit. Nothing is confirmed yet.
  onPending: () => void;
}

interface StartTransferJobParams {
  jobCreate: RoutingJobCreate;
  vehicleCreates: RoutingVehicleCreate[];
  stopDates: string[];
}

function isApplied(status?: RoutingJobStatus | null): boolean {
  return (
    status === RoutingJobStatus.Ready || status === RoutingJobStatus.Confirmed
  );
}

function isFailed(status?: RoutingJobStatus | null): boolean {
  return (
    status === RoutingJobStatus.OptimizationFailed ||
    status === RoutingJobStatus.Cancelled
  );
}

/**
 * Creates the quick-dispatch routing job for a transfer and follows it until
 * it is applied.
 *
 * Quick dispatch optimizes and approves itself, but the mutation can still
 * hand back a queued job, so it is polled until the assignment has actually
 * been applied, has failed, or the wait limit passes.
 */
export function useTransferRoutingJob({
  onApplied,
  onPending,
}: UseTransferRoutingJobParams) {
  const { courierId } = useAuth();
  const { showToast } = useCourieStore();
  const [pendingJob, setPendingJob] = useState<PendingJob | null>(null);
  // The job outcome is reported exactly once. Callers pass fresh callbacks on
  // every render, and reporting the outcome (a toast, closing the modal)
  // re-renders them synchronously before the pending state clears — so the
  // callbacks live in refs, off the effect dependencies, and the settled job
  // is remembered so a re-run cannot report it again.
  const onAppliedRef = useRef(onApplied);
  const onPendingRef = useRef(onPending);
  const settledJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    onAppliedRef.current = onApplied;
    onPendingRef.current = onPending;
  });

  const [createRoutingJob, { loading: isCreatingJob }] =
    useCreateRoutingJobMutation();

  const { data: pendingJobData } = useRoutingJobByIdQuery({
    variables: { courierId: courierId ?? "", id: pendingJob?.id ?? "" },
    skip: !pendingJob || !courierId,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: "network-only",
  });

  const settleJob = useCallback((job: PendingJob): boolean => {
    if (settledJobIdRef.current === job.id) return false;
    settledJobIdRef.current = job.id;
    setPendingJob(null);
    return true;
  }, []);

  useEffect(() => {
    if (!pendingJob) return;
    const status = pendingJobData?.routingJobById?.status;
    if (isApplied(status)) {
      if (settleJob(pendingJob)) onAppliedRef.current(pendingJob.stopDates);
    } else if (isFailed(status)) {
      if (settleJob(pendingJob)) {
        showToast({
          type: "error",
          message: "The transfer failed. Please try again.",
        });
      }
    }
  }, [pendingJob, pendingJobData, settleJob, showToast]);

  // Don't hold the modal open forever if the job stays queued. This is not a
  // success: the job may still fail, so hand off without opening the route.
  useEffect(() => {
    if (!pendingJob) return;
    const timeout = setTimeout(() => {
      if (!settleJob(pendingJob)) return;
      showToast({ type: "info", message: PENDING_MESSAGE });
      onPendingRef.current();
    }, PENDING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [pendingJob, settleJob, showToast]);

  const startTransferJob = useCallback(
    ({ jobCreate, vehicleCreates, stopDates }: StartTransferJobParams) =>
      createRoutingJob({
        variables: {
          courierId: jobCreate.courierId,
          jobCreate,
          vehicleCreates,
        },
      }).then((response) => {
        const job = response.data?.createRoutingJob;
        if (isApplied(job?.status)) {
          onAppliedRef.current(stopDates);
          return;
        }
        if (job?.id) {
          setPendingJob({ id: job.id, stopDates });
          return;
        }
        showToast({ type: "info", message: PENDING_MESSAGE });
        onPendingRef.current();
      }),
    [createRoutingJob, showToast]
  );

  const resetTransferJob = useCallback(() => setPendingJob(null), []);

  return {
    startTransferJob,
    resetTransferJob,
    isTransferJobRunning: isCreatingJob || !!pendingJob,
  };
}
